import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminUsersList = ({ onSelectCreateLicense }) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await fetch(`${BACKEND_URL}/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ active: !currentStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      const data = await response.json();
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, active: data.user.active } : user
        )
      );
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const company = (user.company || '').toLowerCase();
    return fullName.includes(term) || email.includes(term) || company.includes(term);
  });

  if (loading) return <p style={{ textAlign: 'center', padding: '20px' }}>Loading users list...</p>;
  if (error) return <p style={{ color: 'red', textAlign: 'center' }}>Error: {error}</p>;

  return (
    <div className="licenses">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 className="title-blue" style={{ margin: 0 }}>Registered Users ({users.length})</h1>
        <input
          type="text"
          className="input"
          placeholder="Search by name, email, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '300px', margin: 0 }}
        />
      </div>

      <table className="license-table">
        <thead>
          <tr>
            <th>User Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Company</th>
            <th>Licenses Count</th>
            <th>Account Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                No users found matching "{searchTerm}"
              </td>
            </tr>
          ) : (
            filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.firstName} {user.lastName}</strong>
                </td>
                <td>{user.email}</td>
                <td>
                  <span
                    style={{
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor:
                        user.role === 'ADMIN'
                          ? '#e9d8fd'
                          : user.role === 'BUSINESS'
                          ? '#c3dafe'
                          : '#e2e8f0',
                      color:
                        user.role === 'ADMIN'
                          ? '#553c9a'
                          : user.role === 'BUSINESS'
                          ? '#2c5282'
                          : '#4a5568',
                    }}
                  >
                    {user.role}
                  </span>
                </td>
                <td>{user.company || '—'}</td>
                <td>
                  <strong>{user._count?.licenses || 0}</strong>
                </td>
                <td>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: user.active ? '#def7ec' : '#fde8e8',
                      color: user.active ? '#03543f' : '#9b1c1c',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    {user.active ? '● Active' : '○ Deactivated'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleToggleStatus(user.id, user.active)}
                      disabled={actionLoading[user.id]}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        backgroundColor: user.active ? '#e53e3e' : '#38a169',
                        color: '#fff',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {actionLoading[user.id]
                        ? 'Updating...'
                        : user.active
                        ? 'Deactivate'
                        : 'Activate'}
                    </button>

                    <button
                      onClick={() => onSelectCreateLicense && onSelectCreateLicense(user.id)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        backgroundColor: '#214DA0',
                        color: '#fff',
                      }}
                    >
                      + Create License
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminUsersList;
